import React, { useState, lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, CircularProgress, useTheme, useMediaQuery, Drawer, Fab } from '@mui/material';
import { Edit as ComposeIcon } from '@mui/icons-material';
import HybridHeader from './HybridHeader';
import HybridSidebar from './HybridSidebar';
import RightActionBar from './RightActionBar';
import GlobalComposeTabs from '../compose/GlobalComposeTabs';
import { useComposeTabsStore } from '../../stores/composeTabsStore';
import TeamChatPage from '../chat/TeamChatPage';
import DocsPage from '../../pages/docs/DocsPage';

const ComposePage = lazy(() => import('../../pages/compose/ComposePage'));

const HybridMailLayout: React.FC = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md')); // Mobile: < 900px
  const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down('sm')); // Small mobile: < 600px
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const { showingCompose, chatOpen, viewingChat, docsOpen, viewingDocs, sheetsOpen, viewingSheets, getOrCreateTab } = useComposeTabsStore();
  const [restored, setRestored] = useState(false);

  // Restore state on mount - ensure persisted chatOpen/showingCompose is respected
  React.useEffect(() => {
    if (!restored) {
      const state = useComposeTabsStore.getState();
      console.log('[HybridMailLayout] Mount - restoring state. chatOpen:', state.chatOpen, 'viewingChat:', state.viewingChat, 'showingCompose:', state.showingCompose, 'tabs:', state.tabs.length);
      setRestored(true);
      // State is already in the store, just log it
    }
  }, [restored]);

  // Debug logging
  React.useEffect(() => {
    console.log('[HybridMailLayout] chatOpen:', chatOpen, 'viewingChat:', viewingChat, 'showingCompose:', showingCompose);
  }, [chatOpen, viewingChat, showingCompose]);

  const handleMenuClick = () => {
    if (isMobile) {
      setMobileDrawerOpen(!mobileDrawerOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleComposeClick = () => {
    getOrCreateTab();
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        background: muiTheme.palette.background.default,
      }}
    >
      {/* Header */}
      <HybridHeader onMenuClick={handleMenuClick} isMobile={isMobile} />

      {/* Main Container - 3-pane layout on desktop, single pane on mobile */}
      <Box
        className="main-container"
        sx={{
          display: 'flex',
          height: isMobile ? 'calc(100vh - 56px)' : 'calc(100vh - 64px)',
          overflow: 'visible',
          paddingRight: isMobile ? 0 : '48px', // No right action bar on mobile
        }}
      >
        {/* Sidebar - Drawer on mobile, fixed on desktop */}
        {isMobile ? (
          <Drawer
            anchor="left"
            open={mobileDrawerOpen}
            onClose={handleMobileDrawerClose}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
            PaperProps={{
              sx: {
                width: isSmallMobile ? '85vw' : '320px',
                maxWidth: '90vw',
                bgcolor: 'background.paper',
              },
            }}
          >
            <HybridSidebar collapsed={false} onItemClick={handleMobileDrawerClose} isMobile={true} />
          </Drawer>
        ) : (
          <HybridSidebar collapsed={sidebarCollapsed} />
        )}

        {/* Content Area - Shows either mail view or compose */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            bgcolor: 'background.paper',
            width: isMobile ? '100%' : undefined,
          }}
        >
          {/* Compose Tab Bar - shown when compose tabs exist */}
          {!isMobile && <GlobalComposeTabs />}

          {/* Mail view - hidden when compose is showing or viewing chat/docs/sheets */}
          <Box sx={{ display: (showingCompose || viewingChat || viewingDocs || viewingSheets) ? 'none' : 'flex', flexDirection: 'column', flexGrow: 1, width: '100%', overflow: 'hidden' }}>
            <Outlet />
          </Box>

          {/* Chat view - shown when chat is open and viewing chat */}
          {chatOpen && viewingChat && (
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
              <TeamChatPage />
            </Box>
          )}

          {/* Documents view */}
          {docsOpen && viewingDocs && (
            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <DocsPage documentType="document" />
            </Box>
          )}

          {/* Spreadsheets view */}
          {sheetsOpen && viewingSheets && (
            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <DocsPage documentType="spreadsheet" />
            </Box>
          )}

          {/* Compose view - shown when a compose tab is active */}
          {showingCompose && (
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}><CircularProgress /></Box>}>
              <ComposePage />
            </Suspense>
          )}
        </Box>

        {/* Right Action Bar - hidden on mobile */}
        {!isMobile && <RightActionBar width={48} />}
      </Box>

      {/* Floating Action Button for Compose - Mobile only */}
      {isMobile && !showingCompose && !chatOpen && (
        <Fab
          color="primary"
          aria-label="compose"
          onClick={handleComposeClick}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 16,
            zIndex: 1000,
            background: '#1a73e8',
            boxShadow: '0 4px 12px rgba(26,115,232,0.4)',
            '&:hover': {
              background: '#1557b0',
              transform: 'scale(1.05)',
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <ComposeIcon />
        </Fab>
      )}
    </Box>
  );
};

export default HybridMailLayout;
